import React from 'react';
import styled from 'styled-components';

import {
  BigCategoryCard,
  LineChart,
  Row,
  Select,
} from '@zero5/ui';
import { DateRange } from '@zero5/ui/lib/Chart';

import useRevenueTrendQuery from '@/api/revenue/useRevenueTrendQuery';
import { RevenueTrendAggregationPeriod } from '@/api/revenue/models';

import RangePicker from '@/components/common/RangePicker';

import useDateModule from '@/utils/date/useDateModule';

interface Props {
  dateRange: DateRange;
  onDateRangeChange: (dateRange: DateRange) => void;
}

const isAggregationAvailable = (aggregation: RevenueTrendAggregationPeriod, range: DateRange) => {
  const monthlyInMilliseconds = 91 * 24 * 60 * 60 * 1000;
  const weeklyInMilliseconds = 14 * 24 * 60 * 60 * 1000;

  const rangeInMilliseconds = range.end.getTime() - range.start.getTime();

  switch (aggregation) {
    case 'daily': 
      return true;
    case 'weekly':
      return rangeInMilliseconds >= weeklyInMilliseconds;
    case 'monthly':
      return rangeInMilliseconds >= monthlyInMilliseconds;
  }
};

const RevenueTrendChart: React.FC<Props> = ({ dateRange, onDateRangeChange }) => {
  const {
    z5DateModule,
    formatSlashDateChart,
  } = useDateModule();

  const [
    revenueTrendChartAggregation,
    setRevenueTrendChartAggregation,
  ] = React.useState<RevenueTrendAggregationPeriod>('daily');

  const revenueTrendChartAggregatedOptions = React.useMemo(() => {
    const options: Array<{
      label: string;
      value: RevenueTrendAggregationPeriod;
    }> = [];
    
    if (isAggregationAvailable('daily', dateRange)) {
      options.push({
        label: 'Daily',
        value: 'daily',
      });
    }

    if (isAggregationAvailable('weekly', dateRange)) {
      options.push({
        label: 'Weekly',
        value: 'weekly',
      });
    }

    if (isAggregationAvailable('monthly', dateRange)) {
      options.push({
        label: 'Monthly',
        value: 'monthly',
      });
    }

    return options;
  }, [dateRange]);

  const trendDateRangeChangeHandler = React.useCallback(
    (selectedRange: DateRange, aggregation: RevenueTrendAggregationPeriod) => {
      const endOfToday = new Date(z5DateModule.fromUtc(Date.now()).dayEnd().getTimestamp());

      switch (aggregation) {
        case 'weekly':
          if (isAggregationAvailable('weekly', selectedRange)) {
            const newStart = new Date(z5DateModule.fromUtc(selectedRange.start.getTime()).weekStart().getTimestamp());
            const newEnd = new Date(
              Math.min(
                z5DateModule.fromUtc(selectedRange.end.getTime()).weekEnd().getTimestamp(),
                endOfToday.getTime(),
              ),
            );
    
            const newRange = {
              start: newStart,
              end: newEnd,
            };
    
            // eslint-disable-next-line no-param-reassign
            selectedRange = newRange;
          } else {
            setRevenueTrendChartAggregation('daily');
          }
          break;
        case 'monthly':
          if (isAggregationAvailable('monthly', selectedRange)) {
            const newStart = new Date(z5DateModule.fromUtc(selectedRange.start.getTime()).monthStart().getTimestamp());
            const newEnd = new Date(
              Math.min(
                z5DateModule.fromUtc(selectedRange.end.getTime()).monthEnd().getTimestamp(),
                endOfToday.getTime(),
              ),
            );
    
            const newRange = {
              start: newStart,
              end: newEnd,
            };

            // eslint-disable-next-line no-param-reassign
            selectedRange = newRange;
          } else {
            setRevenueTrendChartAggregation('daily');
          }
      }

      onDateRangeChange(selectedRange);
    },
    [onDateRangeChange, z5DateModule],
  );

  const {
    data: revenueTrendData,
    isLoading: revenueTrendLoading,
  } = useRevenueTrendQuery({
    from: dateRange.start.getTime(),
    until: dateRange.end.getTime(),
    aggregationPeriod: revenueTrendChartAggregation,
  });

  const revenueValueArray = revenueTrendData?.map(({ value }) => value) || [];
  const revenueDateArray = revenueTrendData?.map(({ date }) => formatSlashDateChart(date)) || [];

  return (
    <BigCategoryCard
      title="Revenue Trend"
      gap="10px"
      data-test="revenue-trend"
      headerContent={
        <Row wrap="wrap" gap="10px">
          <RangePicker
            dateRange={dateRange}
            onChange={(range) => trendDateRangeChangeHandler(range, revenueTrendChartAggregation)}
          />
          <StyledSelect
            options={revenueTrendChartAggregatedOptions}
            value={revenueTrendChartAggregatedOptions.find(({ value }) => value === revenueTrendChartAggregation)}
            onChange={(option) => {
              trendDateRangeChangeHandler(dateRange, option!.value);
              setRevenueTrendChartAggregation(option!.value);
            }}
          />
        </Row>
      }
    >
      <LineChart
        data={revenueValueArray}
        labels={revenueDateArray}
        isLoading={revenueTrendLoading}
        priceFormat
      />
    </BigCategoryCard>
  );
};

const StyledSelect = styled(Select)`
  min-width: 115px;
` as typeof Select;

export default RevenueTrendChart;